/** @format */

import { PLANNING_DEFAULT_START } from './constants';
import { s2d } from './time';

export const ISO_WEEK_MIN = 1;
export const ISO_WEEK_MAX = 53;

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function emptyPlanningStartConfig() {
  return {
    id: 1,
    defaultStart: PLANNING_DEFAULT_START,
    seasonalEnabled: false,
    periods: [],
  };
}

function hhmm(value) {
  const text = String(value ?? '').trim().slice(0, 5);
  return TIME_PATTERN.test(text) ? text : '';
}

function toLocalDate(input) {
  if (!input) return new Date();
  if (input instanceof Date && !Number.isNaN(input.getTime())) return input;
  const text = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return s2d(text.slice(0, 10));
  const parsed = new Date(input);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

/** ISO week number (1–53) for a Date or YYYY-MM-DD string. */
export function isoWeekNumber(input) {
  const date = toLocalDate(input);
  const utc = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc - yearStart) / 86400000 + 1) / 7);
}

function periodFromRaw(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const weekStart = Number(raw.weekStart ?? raw.week_start);
  const weekEnd = Number(raw.weekEnd ?? raw.week_end);
  const start = hhmm(raw.start ?? raw.start_time);
  if (
    !Number.isInteger(weekStart) ||
    !Number.isInteger(weekEnd) ||
    weekStart < ISO_WEEK_MIN ||
    weekEnd > ISO_WEEK_MAX ||
    weekStart > weekEnd ||
    !start
  ) {
    return null;
  }
  return { weekStart, weekEnd, start };
}

/** Config object used by the planning UI and the settings draft baseline. */
export function normalizePlanningStartConfig(config) {
  const base = emptyPlanningStartConfig();
  if (!config || typeof config !== 'object') return base;
  const defaultStart =
    hhmm(config.defaultStart ?? config.default_start) || base.defaultStart;
  const rawPeriods = config.periods ?? config.seasonal_periods ?? [];
  const periods = Array.isArray(rawPeriods)
    ? rawPeriods.map(periodFromRaw).filter(Boolean)
    : [];
  return {
    id: config.id ?? base.id,
    defaultStart,
    seasonalEnabled: Boolean(
      config.seasonalEnabled ?? config.seasonal_start_enabled,
    ),
    periods,
  };
}

/**
 * Start hour (HH:MM) for a day.
 * Falls back to the configured default when seasonal mode is off or the week is uncovered.
 */
export function resolvePlanningDefaultStart(config, date) {
  const normalized = normalizePlanningStartConfig(config);
  if (!normalized.seasonalEnabled) return normalized.defaultStart;
  const week = isoWeekNumber(date);
  const period = normalized.periods.find(
    (row) => week >= row.weekStart && week <= row.weekEnd,
  );
  return period?.start || normalized.defaultStart;
}

/** First uncovered ISO-week span, or null when weeks 1–53 are already taken. */
export function nextSeasonalPeriod(periods, fallbackStart) {
  const covered = new Set();
  (periods || []).forEach((period) => {
    const start = Number(period.weekStart);
    const end = Number(period.weekEnd);
    if (!Number.isInteger(start) || !Number.isInteger(end)) return;
    for (let week = start; week <= end; week += 1) covered.add(week);
  });
  let gapStart = null;
  for (let week = ISO_WEEK_MIN; week <= ISO_WEEK_MAX; week += 1) {
    if (!covered.has(week)) {
      if (gapStart == null) gapStart = week;
    } else if (gapStart != null) {
      return {
        weekStart: gapStart,
        weekEnd: week - 1,
        start: hhmm(fallbackStart) || PLANNING_DEFAULT_START,
      };
    }
  }
  if (gapStart == null) return null;
  return {
    weekStart: gapStart,
    weekEnd: ISO_WEEK_MAX,
    start: hhmm(fallbackStart) || PLANNING_DEFAULT_START,
  };
}

/**
 * Error message for a seasonal draft, or '' when the periods can be saved.
 * Incomplete rows are reported so the user can fix them before save.
 */
export function seasonalPeriodsError(periods) {
  const rows = periods || [];
  const parsed = [];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index] || {};
    const weekStart = Number(row.weekStart);
    const weekEnd = Number(row.weekEnd);
    const start = hhmm(row.start);
    const label = `Période ${index + 1}`;
    if (
      !Number.isInteger(weekStart) ||
      !Number.isInteger(weekEnd) ||
      weekStart < ISO_WEEK_MIN ||
      weekStart > ISO_WEEK_MAX ||
      weekEnd < ISO_WEEK_MIN ||
      weekEnd > ISO_WEEK_MAX
    ) {
      return `${label} : les semaines doivent être entre 1 et 53.`;
    }
    if (weekStart > weekEnd) {
      return `${label} : la semaine de fin doit être supérieure ou égale à la semaine de début.`;
    }
    if (!start) return `${label} : heure de début invalide.`;
    parsed.push({ weekStart, weekEnd });
  }
  const ordered = [...parsed].sort(
    (a, b) => a.weekStart - b.weekStart || a.weekEnd - b.weekEnd,
  );
  for (let index = 1; index < ordered.length; index += 1) {
    const previous = ordered[index - 1];
    const current = ordered[index];
    if (current.weekStart <= previous.weekEnd) {
      return `Les périodes S${previous.weekStart}–S${previous.weekEnd} et S${current.weekStart}–S${current.weekEnd} se chevauchent.`;
    }
  }
  return '';
}
